import { useEffect, useRef, useState } from "react"
import dayjs from "dayjs"
import type { Dayjs } from "dayjs"
import {
  DatabaseOutlined,
  InfoCircleOutlined,
  SearchOutlined,
} from "@ant-design/icons"
import {
  Button,
  DatePicker,
  Descriptions,
  Flex,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Spin,
  Table,
  Tag,
  Typography,
  message,
} from "antd"
import { Alert } from "~/components/Alert"
import type { TableProps } from "antd"
import {
  createStaffGiftOpLog,
  createStaffLevelGiftOpLog,
  getStaffGiftOpDetail,
  listStaffGiftOpLogs,
  revokeStaffLevelGift,
} from "~/api/staff"
import { MOBILE_PHONE_PATTERN, ProductCode } from "~/common/constants"
import { GiftDetails } from "./Details"
import { formatDateTimeMinute } from "~/common/utils"
import {
  giftStatusColor,
  giftStatusLabel,
  giftTypeColor,
  giftTypeLabel,
} from "~/pages/Staff/labels"
import { useStaffUserLookup } from "~/pages/Staff/useStaffUserLookup"
import { StaffUserCard } from "~/pages/Staff/StaffUserCard"
import { usePagedQuery } from "~/pages/Staff/usePagedQuery"
import styles from "./index.css"

const { Text, Title } = Typography

type StaffGiftFormValues = {
  expiresAt: Dayjs
  levelRemark?: string
  mobile: string
  walletRechargeAmount: number
  walletRemark?: string
}

type RevokeGiftFormValues = {
  reason: string
}

const staffGiftPageTypes: API.Staff.StaffGiftType[] = [
  "WALLET_POINTS",
  "LEVEL_PACKAGE",
]

const disabledExpiredDate = (current: Dayjs) => {
  return current.isBefore(dayjs().startOf("day"))
}

const getGiftContent = (record: API.Staff.StaffGiftOpLog) => {
  return record.gift_type === "LEVEL_PACKAGE"
    ? "专业版 1 个月（含 300 积点）"
    : `${record.wallet_recharge_amount || 0} 积点`
}

const getRelationId = (record: API.Staff.StaffGiftOpLog) => {
  return record.gift_type === "LEVEL_PACKAGE"
    ? record.order_id
    : record.wallet_recharge_record_id
}

export const StaffGift = () => {
  const [form] = Form.useForm<StaffGiftFormValues>()
  const [revokeForm] = Form.useForm<RevokeGiftFormValues>()
  const [giftType, setGiftType] =
    useState<API.Staff.StaffGiftType>("WALLET_POINTS")
  const {
    queriedUser,
    queryingUser,
    lookupUser,
    resetQueriedUser,
    setQueriedUser,
  } = useStaffUserLookup()
  const [submittingWalletGift, setSubmittingWalletGift] = useState(false)
  const [submittingLevelGift, setSubmittingLevelGift] = useState(false)
  const [giftDetail, setGiftDetail] =
    useState<API.Staff.StaffGiftOpLogDetailResponse | null>(null)
  const [giftDetailOpen, setGiftDetailOpen] = useState(false)
  const [loadingGiftDetail, setLoadingGiftDetail] = useState(false)
  const [revokeDetail, setRevokeDetail] =
    useState<API.Staff.StaffGiftOpLogDetailResponse | null>(null)
  const [revokeOpen, setRevokeOpen] = useState(false)
  const [loadingRevokeDetail, setLoadingRevokeDetail] = useState(false)
  const [submittingRevoke, setSubmittingRevoke] = useState(false)
  const revokeDetailControllerRef = useRef<AbortController | null>(null)

  const giftLogs = usePagedQuery({
    fetchPage: listStaffGiftOpLogs,
    initialFilters: { gift_types: staffGiftPageTypes },
  })

  const queryUser = async () => {
    const { mobile } = await form.validateFields(["mobile"])
    await lookupUser(mobile)
  }

  const openGiftDetail = async (record: API.Staff.StaffGiftOpLog) => {
    if (!record.id) {
      void message.warning("缺少赠送记录ID")
      return
    }

    setGiftDetailOpen(true)
    setGiftDetail(null)
    setLoadingGiftDetail(true)

    try {
      const response = await getStaffGiftOpDetail({
        op_log_id: record.id,
      })
      setGiftDetail(response.data || null)
    } finally {
      setLoadingGiftDetail(false)
    }
  }

  const closeGiftDetail = () => {
    setGiftDetailOpen(false)
    setGiftDetail(null)
  }

  const openRevoke = async (record: API.Staff.StaffGiftOpLog) => {
    if (!record.id) {
      void message.warning("缺少赠送记录ID")
      return
    }

    revokeDetailControllerRef.current?.abort()
    const controller = new AbortController()
    revokeDetailControllerRef.current = controller

    setRevokeOpen(true)
    setRevokeDetail(null)
    setLoadingRevokeDetail(true)

    try {
      const response = await getStaffGiftOpDetail(
        {
          op_log_id: record.id,
        },
        controller.signal,
      )
      if (controller.signal.aborted) return

      const detail = response.data

      if (!detail) {
        void message.error("赠送详情为空，请刷新列表后重试")
        setRevokeOpen(false)
        return
      }

      setRevokeDetail(detail)
    } catch {
      if (controller.signal.aborted) return

      setRevokeOpen(false)
    } finally {
      if (revokeDetailControllerRef.current === controller) {
        revokeDetailControllerRef.current = null
        setLoadingRevokeDetail(false)
      }
    }
  }

  const closeRevoke = () => {
    revokeDetailControllerRef.current?.abort()
    revokeDetailControllerRef.current = null
    setRevokeOpen(false)
    setRevokeDetail(null)
    setLoadingRevokeDetail(false)
  }

  const confirmRevoke = async () => {
    const opLogId = revokeDetail?.op_log.id
    if (!opLogId) {
      void message.error("缺少赠送记录ID，请刷新列表后重试")
      return
    }

    const values = await revokeForm.validateFields()
    setSubmittingRevoke(true)

    try {
      const response = await revokeStaffLevelGift({
        op_log_id: opLogId,
        reason: values.reason.trim(),
      })
      const result = response.data

      if (!result) {
        void message.error("撤销结果为空，请刷新列表后确认状态")
        return
      }

      if (giftDetail?.op_log.id === opLogId) {
        setGiftDetail({
          op_log: result.op_log,
          order: result.order,
          payment_trade: result.payment_trade,
          wallet_recharge_record: null,
          wallet_recharge_records: result.wallet_recharge_records,
        })
      }

      setQueriedUser((currentUser) =>
        currentUser?.id === result.op_log.user_id
          ? { ...currentUser, level_info: result.level_info }
          : currentUser,
      )
      void message.success("专业版赠送已撤销")
      closeRevoke()
      giftLogs.refresh()
    } finally {
      setSubmittingRevoke(false)
    }
  }

  const requireQueriedUser = () => {
    if (!queriedUser?.id) {
      void message.warning("请先查询并确认被赠送用户")
      return null
    }

    return queriedUser
  }

  const confirmWalletGift = async () => {
    const values = await form.validateFields([
      "mobile",
      "walletRechargeAmount",
      "expiresAt",
      "walletRemark",
    ])
    const user = requireQueriedUser()
    if (!user?.id) return

    const payload = {
      user_id: user.id,
      wallet_recharge_amount: values.walletRechargeAmount,
      expires_at: values.expiresAt.unix(),
      remark: values.walletRemark?.trim() || "",
    } satisfies API.Staff.CreateStaffGiftOpLogParams

    Modal.confirm({
      title: "确认赠送积点",
      content: (
        <Flex
          vertical
          gap={8}
        >
          <Text>被赠送用户：{user.mobile}</Text>
          <Text>赠送积点：{payload.wallet_recharge_amount}</Text>
          <Text>
            到期时间：
            {formatDateTimeMinute(payload.expires_at)}
          </Text>
          <Text>备注：{payload.remark || "-"}</Text>
        </Flex>
      ),
      okText: "确认赠送",
      cancelText: "取消",
      onOk: async () => {
        setSubmittingWalletGift(true)

        try {
          await createStaffGiftOpLog(payload)
          void message.success("积点已赠送")
          form.resetFields([
            "walletRechargeAmount",
            "expiresAt",
            "walletRemark",
          ])
          giftLogs.search(giftLogs.filters)
        } finally {
          setSubmittingWalletGift(false)
        }
      },
    })
  }

  const confirmLevelGift = async () => {
    const values = await form.validateFields(["mobile", "levelRemark"])
    const user = requireQueriedUser()
    if (!user?.id) return

    const payload = {
      user_id: user.id,
      product_code: ProductCode.proMonthlyGiftV1,
      remark: values.levelRemark?.trim() || "",
    } satisfies API.Staff.CreateStaffLevelGiftOpLogParams

    Modal.confirm({
      title: "确认赠送专业版 1 个月",
      content: (
        <Flex
          vertical
          gap={8}
        >
          <Text>被赠送用户：{user.mobile}</Text>
          <Text>赠送权益：专业版 1 个月，含 300 积点</Text>
          <Text>生效方式：创建 0 元内部赠送订单并立即生效</Text>
          <Text>备注：{payload.remark || "-"}</Text>
          <Text type="warning">
            请再次核对用户；赠送成功后可在下方记录中执行撤销。
          </Text>
        </Flex>
      ),
      okText: "确认赠送专业版",
      cancelText: "取消",
      onOk: async () => {
        setSubmittingLevelGift(true)

        try {
          await createStaffLevelGiftOpLog(payload)
          void message.success("专业版 1 个月已赠送")
          form.resetFields(["levelRemark"])
          setQueriedUser(null)
          giftLogs.search(giftLogs.filters)
        } finally {
          setSubmittingLevelGift(false)
        }
      },
    })
  }

  const resetForm = () => {
    form.resetFields()
    setQueriedUser(null)
  }

  const columns: TableProps<API.Staff.StaffGiftOpLog>["columns"] = [
    {
      title: "赠送时间",
      dataIndex: "created_t",
      width: 170,
      render: (value?: string) => formatDateTimeMinute(value),
    },
    {
      title: "赠送类型",
      dataIndex: "gift_type",
      width: 130,
      render: (value: API.Staff.StaffGiftType) => (
        <Tag color={giftTypeColor[value]}>{giftTypeLabel[value]}</Tag>
      ),
    },
    {
      title: "被赠送手机号",
      dataIndex: "user_mobile",
      width: 140,
    },
    {
      title: "赠送内容",
      width: 220,
      render: (_, record) => getGiftContent(record),
    },
    {
      title: "状态",
      dataIndex: "gift_status",
      width: 90,
      render: (value: API.Staff.StaffGiftStatus) => (
        <Tag color={giftStatusColor[value]}>{giftStatusLabel[value]}</Tag>
      ),
    },
    {
      title: "积点到期时间",
      dataIndex: "expires_at",
      width: 170,
      render: (value: number | null | undefined, record) =>
        record.gift_type === "LEVEL_PACKAGE"
          ? "随专业版权益周期"
          : formatDateTimeMinute(value),
    },
    {
      title: "用户ID",
      dataIndex: "user_id",
      ellipsis: true,
    },
    {
      title: "关联记录ID",
      ellipsis: true,
      render: (_, record) => getRelationId(record) || "-",
    },
    {
      title: "备注",
      dataIndex: "remark",
      ellipsis: true,
      render: (value?: string) => value || "-",
    },
    {
      title: "操作",
      width: 140,
      fixed: "right",
      render: (_, record) => (
        <Flex gap={4}>
          <Button
            type="link"
            onClick={(event) => {
              event.stopPropagation()
              void openGiftDetail(record)
            }}
          >
            详情
          </Button>
          {record.gift_type === "LEVEL_PACKAGE" &&
          record.gift_status === "GRANTED" ? (
            <Button
              danger
              type="link"
              onClick={(event) => {
                event.stopPropagation()
                void openRevoke(record)
              }}
            >
              撤销
            </Button>
          ) : null}
        </Flex>
      ),
    },
  ]

  useEffect(
    () => () => {
      revokeDetailControllerRef.current?.abort()
    },
    [],
  )

  const isWalletGift = giftType === "WALLET_POINTS"

  return (
    <Flex
      className={styles.page}
      vertical
      gap={18}
    >
      <Flex
        vertical
        gap={6}
      >
        <Title level={2}>Staff权益赠送</Title>
        <Text type="secondary">
          为指定用户赠送积点或专业版会员，所有操作统一记录并可追溯关联业务链路。
        </Text>
      </Flex>

      <Flex
        className={styles.workspace}
        align="stretch"
        gap={18}
      >
        <section className={styles.formPanel}>
          <Form<StaffGiftFormValues>
            form={form}
            colon={false}
            labelAlign="left"
            labelCol={{ flex: "150px" }}
            requiredMark
          >
            <Form.Item label="赠送类型">
              <Select<API.Staff.StaffGiftType>
                className={styles.fullWidth}
                options={[
                  {
                    label: "赠送积点",
                    value: "WALLET_POINTS",
                  },
                  {
                    label: "赠送专业版",
                    value: "LEVEL_PACKAGE",
                  },
                ]}
                value={giftType}
                onChange={setGiftType}
              />
            </Form.Item>

            <Form.Item
              label="被赠送手机号"
              required
            >
              <Flex gap={12}>
                <Form.Item
                  className={styles.inlineField}
                  name="mobile"
                  noStyle
                  rules={[
                    { required: true, message: "请输入被赠送用户手机号" },
                    {
                      pattern: MOBILE_PHONE_PATTERN,
                      message: "手机号格式错误",
                    },
                  ]}
                >
                  <Input
                    placeholder="请输入被赠送用户手机号"
                    onChange={resetQueriedUser}
                  />
                </Form.Item>
                <Button
                  icon={<SearchOutlined />}
                  loading={queryingUser}
                  onClick={queryUser}
                >
                  查询用户
                </Button>
              </Flex>
            </Form.Item>

            {queriedUser ? (
              <StaffUserCard
                className={styles.userCard}
                expiryLabel="会员有效期至"
                user={queriedUser}
              />
            ) : null}

            {isWalletGift ? (
              <>
                <Form.Item
                  label="赠送积点"
                  name="walletRechargeAmount"
                  rules={[
                    { required: true, message: "请输入赠送积点数量" },
                    {
                      type: "number",
                      min: 1,
                      max: 2000,
                      message: "单次赠送积点必须在 1 到 2000 之间",
                    },
                  ]}
                >
                  <InputNumber
                    className={styles.fullWidth}
                    min={1}
                    max={2000}
                    precision={0}
                    placeholder="请输入 1 到 2000 的整数"
                  />
                </Form.Item>

                <Form.Item
                  label="到期时间"
                  name="expiresAt"
                  rules={[
                    { required: true, message: "请选择积点到期时间" },
                    {
                      validator: (_rule, value?: Dayjs) => {
                        if (!value || value.isAfter(dayjs())) {
                          return Promise.resolve()
                        }

                        return Promise.reject(
                          new Error("到期时间必须晚于当前时间"),
                        )
                      },
                    },
                  ]}
                >
                  <DatePicker
                    className={styles.fullWidth}
                    disabledDate={disabledExpiredDate}
                    format="YYYY-MM-DD HH:mm"
                    placeholder="请选择积点到期时间"
                    showTime={{ format: "HH:mm" }}
                  />
                </Form.Item>

                <Form.Item
                  label="备注"
                  name="walletRemark"
                  rules={[{ max: 500, message: "备注最多 500 字" }]}
                >
                  <Input.TextArea
                    autoSize={{ minRows: 4, maxRows: 8 }}
                    placeholder="请输入赠送备注，选填"
                    showCount
                    maxLength={500}
                  />
                </Form.Item>

                <Alert
                  className={styles.summary}
                  showIcon
                  title="赠送后会生成一条 Staff积点充值记录，用户可在积点明细中查看。"
                />
              </>
            ) : (
              <>
                <Form.Item label="赠送套餐">
                  <Flex
                    className={styles.packageCard}
                    vertical
                    gap={6}
                  >
                    <Text strong>专业版 1 个月</Text>
                    <Text type="secondary">
                      立即生效，套餐内含 300 积点；订单和支付交易金额均为 0 元。
                    </Text>
                  </Flex>
                </Form.Item>

                <Form.Item
                  label="备注"
                  name="levelRemark"
                  rules={[{ max: 500, message: "备注最多 500 字" }]}
                >
                  <Input.TextArea
                    autoSize={{ minRows: 4, maxRows: 8 }}
                    placeholder="请输入赠送原因，建议填写，最多 500 字"
                    showCount
                    maxLength={500}
                  />
                </Form.Item>

                <Alert
                  className={styles.summary}
                  showIcon
                  title="系统会创建完整的 0 元订单和 Staff赠送支付交易，并立即发放专业版权益。"
                  description="已有生效中、待生效或待支付专业版订单的用户将无法赠送，最终以服务端校验为准。"
                />
              </>
            )}

            <Flex
              justify="flex-end"
              gap={12}
            >
              <Button onClick={resetForm}>取消</Button>
              <Button
                type="primary"
                loading={
                  isWalletGift ? submittingWalletGift : submittingLevelGift
                }
                onClick={() =>
                  void (isWalletGift ? confirmWalletGift() : confirmLevelGift())
                }
              >
                {isWalletGift ? "确认赠送积点" : "确认赠送专业版"}
              </Button>
            </Flex>
          </Form>
        </section>

        <aside className={styles.rulePanel}>
          <Flex
            align="center"
            gap={8}
          >
            <InfoCircleOutlined className={styles.ruleIcon} />
            <Title level={4}>赠送规则</Title>
          </Flex>
          <ul className={styles.ruleList}>
            <li>请先通过手机号查询并确认被赠送用户。</li>
            {isWalletGift ? (
              <>
                <li>单次可赠送 1 到 2000 积点。</li>
                <li>到期时间按秒级时间戳提交给后端。</li>
                <li>操作会直接创建积点充值记录，不生成订单。</li>
              </>
            ) : (
              <>
                <li>当前仅支持赠送专业版 1 个月。</li>
                <li>成功后专业版立即生效，并发放当月 300 积点。</li>
                <li>系统会生成 0 元订单、支付交易和赠送交易流水号。</li>
                <li>专业版生效中或存在待处理订单时不可重复赠送。</li>
              </>
            )}
            <li>备注会随赠送记录保存，便于后续审计核对。</li>
          </ul>
        </aside>
      </Flex>

      <section className={styles.logPanel}>
        <Flex
          align="center"
          justify="space-between"
        >
          <Flex
            align="center"
            gap={8}
          >
            <DatabaseOutlined className={styles.sectionIcon} />
            <Title level={4}>Staff赠送记录</Title>
          </Flex>
          <Button onClick={giftLogs.refresh}>刷新</Button>
        </Flex>
        <Table
          columns={columns}
          {...giftLogs.tableProps}
          rowKey={(record) =>
            record.id ||
            `${record.user_id}-${record.created_t}-${record.gift_type}`
          }
          rowClassName={styles.logRow}
          onRow={(record) => ({
            onClick: () => {
              void openGiftDetail(record)
            },
          })}
          scroll={{ x: 1_420 }}
        />
      </section>

      <Modal
        destroyOnHidden
        footer={null}
        open={giftDetailOpen}
        title="赠送详情"
        width={1_100}
        onCancel={closeGiftDetail}
      >
        <Spin spinning={loadingGiftDetail}>
          {giftDetail ? <GiftDetails detail={giftDetail} /> : null}
        </Spin>
      </Modal>

      <Modal
        cancelButtonProps={{ disabled: submittingRevoke }}
        cancelText="取消"
        closable={!submittingRevoke}
        destroyOnHidden
        maskClosable={!submittingRevoke}
        okButtonProps={{
          danger: true,
          disabled: loadingRevokeDetail || !revokeDetail,
        }}
        okText="确认撤销"
        open={revokeOpen}
        title="撤销专业版赠送"
        confirmLoading={submittingRevoke}
        onCancel={closeRevoke}
        onOk={() => void confirmRevoke()}
      >
        <Spin spinning={loadingRevokeDetail}>
          {revokeDetail ? (
            <Flex
              vertical
              gap={16}
            >
              <Alert
                description="已生效的专业版权益将立即结束，尚未生效的权益将被取消；该订单尚未使用的随赠积点会转入已退额度，已经使用的积点不会追回。存在冻结积点或权益已经到期时，服务端会拒绝操作。"
                showIcon
                title="这是高风险操作，请确认撤销影响"
                level="strong"
              />
              <Descriptions
                bordered
                column={1}
                size="small"
              >
                <Descriptions.Item label="被赠送用户">
                  {revokeDetail.op_log.user_mobile}
                </Descriptions.Item>
                <Descriptions.Item label="当前权益结束时间">
                  {formatDateTimeMinute(
                    revokeDetail.order?.level_package?.actual_level_period_end,
                  )}
                </Descriptions.Item>
                <Descriptions.Item label="剩余随赠积点">
                  {revokeDetail.wallet_recharge_records.reduce(
                    (total, record) => total + record.remaining_amount,
                    0,
                  )}
                </Descriptions.Item>
                <Descriptions.Item label="冻结随赠积点">
                  {revokeDetail.wallet_recharge_records.reduce(
                    (total, record) => total + record.frozen_amount,
                    0,
                  )}
                </Descriptions.Item>
              </Descriptions>
              <Form<RevokeGiftFormValues>
                clearOnDestroy
                form={revokeForm}
                layout="vertical"
              >
                <Form.Item
                  label="撤销原因"
                  name="reason"
                  rules={[
                    { required: true, message: "请输入撤销原因" },
                    {
                      whitespace: true,
                      message: "撤销原因不能为空白字符",
                    },
                    { max: 500, message: "撤销原因最多 500 字" },
                  ]}
                >
                  <Input.TextArea
                    autoSize={{ minRows: 3, maxRows: 6 }}
                    maxLength={500}
                    placeholder="请输入撤销原因，提交后将写入操作记录"
                    showCount
                  />
                </Form.Item>
              </Form>
            </Flex>
          ) : null}
        </Spin>
      </Modal>
    </Flex>
  )
}
